import React from 'react';
import { Box, Typography, IconButton, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { GamifyIcon } from 'application/components/GamifyIcon';
import { getPinsDict } from 'application/routes/config/utils/tempsUtils';
import * as types from 'application/_types/types.type';

const pinItems = getPinsDict();

interface StageCardProps {
  stage: types.StagePartial;
  selectedStage?: types.StagePartial | null;
  onStageClick: (stage: types.StagePartial) => void;
  onAddStage?: (afterStage: types.StagePartial) => void;
  selectedCRM?: types.StageCategoryPartial | null;
}

const PinTag = ({
  dispositionID,
  selectedCRM,
}: {
  dispositionID: string;
  selectedCRM?: types.StageCategoryPartial | null;
}) => {
  const disposition = selectedCRM?.dispositions?.find(
    (d) => d.id === dispositionID
  );
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {!disposition ? (
        <Typography
          sx={(theme) => ({
            fontSize: 14,
            fontWeight: 500,
            lineHeight: '22px',
            color: theme.custom?.text,
          })}
        >
          None
        </Typography>
      ) : (
        <>
          <img
            src={pinItems[disposition.iconName as keyof typeof pinItems]}
            style={{
              width: 20,
              height: 20,
              borderRadius: 50,
              aspectRatio: '1/1',
            }}
            alt={disposition.title}
          />
          <Typography
            sx={(theme) => ({
              fontSize: 14,
              fontWeight: 500,
              lineHeight: '22px',
              color: theme.custom?.text,
            })}
          >
            {disposition.title}
          </Typography>
        </>
      )}
    </Box>
  );
};

const StageCard: React.FC<StageCardProps> = ({
  stage,
  selectedStage,
  onStageClick,
  onAddStage,
  selectedCRM,
}) => {
  const isSelected = selectedStage?.id === stage.id;
  const theme = useTheme();

  return (
    // OUTER: allows overhang
    <Box
      sx={(theme) => ({
        position: 'relative',
        bgcolor: theme.custom?.isDark ? '#1E1E1E' : 'white',
        boxShadow: 1,
        borderRadius: 2,
        border: isSelected
          ? stage.id === 'temp'
            ? '2px solid #34c759'
            : `2px solid ${theme.custom?.secondary}`
          : `1px solid ${theme.custom?.stroke}`,
        maxWidth: 320,
        overflow: 'visible',
        '&:hover': {
          cursor: 'pointer',
          boxShadow: 4,
          transition: 'box-shadow 0.2s ease',
        },
      })}
      onClick={() => onStageClick(stage)}
    >
      {/* INNER CLIPPER: keeps rounded-corner clipping of the content */}
      <Box sx={{ overflow: 'hidden', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 48,
              width: 48,
              borderRadius: 100,
              p: 1,
              bgcolor:
                stage.id === 'temp'
                  ? 'rgba(52, 199, 89, 0.5)'
                  : theme.custom?.isDark
                  ? theme.custom?.background1
                  : '#F5F5F5',
              flexShrink: 0,
            })}
          >
            <GamifyIcon icon="stack" color={theme.custom?.text} />
          </Box>
          <Typography
            sx={(theme) => ({
              fontSize: 16,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: theme.custom?.text,
            })}
          >
            {stage.title}
          </Typography>
          {/* <Typography
            sx={{ fontSize: 14, fontWeight: 500, lineHeight: '22px' }}
          >
            {stage.sequence}
          </Typography> */}
        </Box>

        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: theme.custom?.background2,
            px: 2,
            py: 0.5,
            justifyContent: 'space-between',
          })}
        >
          <Typography
            sx={(theme) => ({
              fontSize: 14,
              fontWeight: 500,
              lineHeight: '22px',
              color: theme.custom?.text,
            })}
          >
            Stage Disposition:
          </Typography>
          <PinTag
            dispositionID={stage.changeToDispositionID ?? ''}
            selectedCRM={selectedCRM}
          />
        </Box>
      </Box>

      {/* RIGHT HOVER ZONE (with outer overhang) */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: '0px', // overhang outside card
          height: '100%',
          width: 'calc(20% + 16px)', // 20% inside + 16px outside
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          pointerEvents: 'auto',
          zIndex: 2,
          '.add-btn': { opacity: 0, transition: 'opacity 0.15s ease' },
          '&:hover .add-btn': { opacity: 1 },
        }}
        // onClick={(e) => e.stopPropagation()} // ignore clicks on the transparent zone
      >
        <IconButton
          className="add-btn"
          size="small"
          aria-label="Add stage after"
          title="Add stage after"
          onClick={(e) => {
            e.stopPropagation();
            onAddStage?.(stage);
          }}
          sx={(theme) => ({
            bgcolor: theme.custom?.background1,
            border: `2px solid ${theme.custom?.primary}`,
            boxShadow: 1,
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translate(50%, -50%)',
            color: theme.custom?.primary,
            '&:hover': {
              bgcolor: theme.custom?.primary,
              color: theme.custom?.textCTA,
            },
          })}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default StageCard;
